// @flow

import {useCallback} from 'react';
import {defineMessages, useIntl} from 'react-intl';

const messages = defineMessages({
  defaultUnitLabel: {
    id: 'docsShell.ib.defaultUnitLabel',
    defaultMessage: 'unit',
  },
  bucketPriceDesc: {
    id: 'docsShell.ib.bucketPrice.description',
    description:
      'Description of a price that handles if the price is for a group of units or for an individual unit',
    defaultMessage: `{bucketQuantity, plural,
      one {{usageType, select, metered {{formattedPrice} per {unitLabel}} other {{formattedPrice}}}}
      other {{formattedPrice} per group of {bucketQuantity, number}}
    }`,
  },
  planPriceDesc: {
    id: 'docsShell.ib.price.description',
    description: `The very short description of what a plan is. In english we use shorthand
      for the singular case ("$4/week" vs "$4 every week"), but it might not make sense in
      your language. In english there are 8 complete sentences, in 2 groups of 4. Please reach
      out if this is too confusing.`,
    defaultMessage: `{intervalCount, plural,
      one   {{intervalUnit, select,
        day   {{formattedPrice} / day}
        week  {{formattedPrice} / week}
        month {{formattedPrice} / month}
        year  {{formattedPrice} / year}
        one_time {{formattedPrice}}
        other {}
      }}
      other {{intervalUnit, select,
        day   {{formattedPrice} every {formattedIntervalCount} days}
        week  {{formattedPrice} every {formattedIntervalCount} weeks}
        month {{formattedPrice} every {formattedIntervalCount} months}
        year  {{formattedPrice} every {formattedIntervalCount} years}
        other {}
      }}
    }`,
  },
});

export const SUBUNIT_TO_UNITS = {
  BHD: 1000,
  BIF: 1,
  CLF: 1,
  CLP: 1,
  DJF: 1,
  GNF: 1,
  IQD: 1000,
  JOD: 1000,
  JPY: 1,
  KMF: 1,
  KRW: 1,
  KWD: 1000,
  LYD: 1000,
  MGA: 1,
  OMR: 1000,
  PYG: 1,
  RWF: 1,
  TND: 1000,
  VND: 1,
  VUV: 1,
  XAF: 1,
  XAG: 1,
  XAU: 1,
  XDR: 1,
  XOF: 1,
  XPF: 1,
  BTC: 100000000,
  MRU: 5,
};

const log10 = (n: number) => Math.log(n) / Math.LN10;
const currencyToDecimalLength = (
  currency: string,
  defaultLength: number = 2,
): number => {
  const subunitToUnit = SUBUNIT_TO_UNITS[currency];
  if (subunitToUnit) {
    return Math.round(log10(subunitToUnit));
  }
  return defaultLength;
};

const formatDecimalPrice = (amount: number, currency: string) => {
  const decimalLength = currencyToDecimalLength(currency);
  return Number(amount) / Math.pow(10, decimalLength);
};

export const useFormatAmount = () => {
  const intl = useIntl();

  return useCallback(
    (amount: number, currency: string): string => {
      const upperCurrency = currency ? currency.toUpperCase() : currency;
      const unitAmount = formatDecimalPrice(amount, upperCurrency);
      return intl.formatNumber(unitAmount || 0.0, {
        style: 'currency',
        currency,
      });
    },
    [intl],
  );
};

export const useFormatPriceAmount = () => {
  const intl = useIntl();
  const formatAmount = useFormatAmount();

  return useCallback(
    (price: Object): string => {
      const {
        recurring,
        currency,
        unit_amount,
        amount,
        transform_quantity: transformUsage,
      } = price;

      const intervalUnit = recurring ? recurring.interval : 'one_time';
      const intervalCount = recurring ? recurring.interval_count : 1;
      const usageType = recurring ? recurring.usage_type : 'licensed';

      let unitLabel;
      if (
        price.product &&
        typeof price.product === 'object' &&
        typeof price.product.unit_label === 'string'
      ) {
        unitLabel = price.product.unit_label;
      } else {
        unitLabel = intl.formatMessage(messages.defaultUnitLabel);
      }

      const formattedPrice = formatAmount(unit_amount || amount, currency);

      const fullPrice = intl.formatMessage(messages.bucketPriceDesc, {
        formattedPrice,
        bucketQuantity: transformUsage?.divide_by || 1,
        unitLabel,
        usageType,
      });

      return intl.formatMessage(messages.planPriceDesc, {
        formattedPrice: fullPrice,
        intervalCount,
        formattedIntervalCount: intl.formatNumber(intervalCount),
        intervalUnit,
      });
    },
    [intl, formatAmount],
  );
};
